import { useEffect, useRef, useCallback, useId } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TaskList from '@tiptap/extension-task-list'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from 'tiptap-markdown'
import type { NotebookNote } from '../../shared/types/notebook'
import type { SlashCommand } from '../../shared/types/chat'
import { RobustTaskItem } from './extensions/RobustTaskItem'
import { WikiLinkNode } from './extensions/WikiLinkNode'
import { wikiLinkData } from './extensions/wikiLinkData'
import { useTiptapSlashMenu } from './useTiptapSlashMenu'
import SlashCommandMenu from './SlashCommandMenu'
import './tiptap-theme.css'

interface TiptapEditorProps {
  value: string
  onChange: (md: string) => void
  onNavigate?: (noteId: string) => void
  allNotes: NotebookNote[]
  slashCommands?: SlashCommand[]
  onExecuteCommand?: (commandId: string) => void
  placeholder?: string
  className?: string
  autoFocus?: boolean
  minHeight?: string
}

export default function TiptapEditor({
  value,
  onChange,
  onNavigate,
  allNotes,
  slashCommands = [],
  onExecuteCommand,
  placeholder = 'Start writing... (type / for commands)',
  className = '',
  autoFocus = false,
  minHeight = '100px',
}: TiptapEditorProps) {
  const lastEmittedRef = useRef(value)
  const allNotesRef = useRef(allNotes)
  useEffect(() => { allNotesRef.current = allNotes }, [allNotes])

  // Tiptap 2.x: editor.view is a getter that THROWS if the ProseMirror view
  // isn't mounted yet. Optional chaining (editor?.view?.dom) does NOT help
  // because ?.  evaluates the getter which throws. Use this helper instead.
  const getEditorDom = useCallback((ed: Editor | null) => {
    try { return ed?.view?.dom ?? null } catch { return null }
  }, [])

  // Keep mutable data in sync for WikiLinkNode NodeView
  useEffect(() => {
    wikiLinkData.allNotes = allNotes
    wikiLinkData.onNavigate = onNavigate
  }, [allNotes, onNavigate])

  const editor = useEditor({
    // Prevent Tiptap from rendering before the React component is mounted.
    // Without this, EditorContent accesses editor.view.dom during the initial
    // render which throws on iOS WKWebView (Sentry TENDBOT-R/S/F).
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: { keepMarks: true },
      }),
      TaskList,
      RobustTaskItem.configure({ nested: false }),
      Placeholder.configure({ placeholder }),
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      WikiLinkNode.configure({ onNavigate }),
    ],
    content: value,
    autofocus: false,
    editorProps: {
      attributes: {
        class: `tiptap ${className}`,
        style: `min-height: ${minHeight}; outline: none;`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const storage = ed.storage as unknown as Record<string, Record<string, unknown>>
      const md = (storage.markdown as { getMarkdown: () => string }).getMarkdown()
      lastEmittedRef.current = md
      onChange(md)
    },
  })

  // Manual autofocus: wait for the editor view to be ready before focusing
  useEffect(() => {
    if (!autoFocus || !editor || editor.isDestroyed) return
    const dom = getEditorDom(editor)
    if (dom) {
      editor.commands.focus('end')
      return
    }
    // View not ready yet — poll briefly until it is
    const timer = setInterval(() => {
      if (editor.isDestroyed) { clearInterval(timer); return }
      if (getEditorDom(editor)) {
        clearInterval(timer)
        editor.commands.focus('end')
      }
    }, 50)
    return () => clearInterval(timer)
  }, [editor, autoFocus, getEditorDom])

  // Sync external value changes (e.g. navigating to a different page)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    if (value === lastEmittedRef.current) return
    lastEmittedRef.current = value
    editor.commands.setContent(value)
  }, [editor, value])

  /* ── Slash menu ── */

  const {
    menuPhase, items, selectedIdx, menuCoords, checkSlash, dismiss,
    handleSelectCommand, handleSelectNote, handleMenuKeyDown,
  } = useTiptapSlashMenu({
    editor,
    slashCommands,
    allNotesRef,
    onExecuteCommand,
  })

  // Dismiss the menu on clicks outside it or on viewport scroll.
  const menuRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (menuPhase === 'closed') return
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (menuRef.current && target && menuRef.current.contains(target)) return
      dismiss()
    }
    const onScroll = () => dismiss()
    document.addEventListener('mousedown', onDocMouseDown, true)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [menuPhase, dismiss])

  // Trigger slash detection on every update
  const onUpdateRef = useRef(checkSlash)
  useEffect(() => { onUpdateRef.current = checkSlash }, [checkSlash])
  useEffect(() => {
    if (!editor) return
    const handler = () => onUpdateRef.current()
    editor.on('update', handler)
    editor.on('selectionUpdate', handler)
    return () => {
      editor.off('update', handler)
      editor.off('selectionUpdate', handler)
    }
  }, [editor])

  // Intercept keyboard when slash menu is open
  const handleMenuKeyDownRef = useRef(handleMenuKeyDown)
  useEffect(() => { handleMenuKeyDownRef.current = handleMenuKeyDown }, [handleMenuKeyDown])

  useEffect(() => {
    const dom = getEditorDom(editor)
    if (!dom) return
    const listener = (e: KeyboardEvent) => {
      if (handleMenuKeyDownRef.current(e)) {
        e.stopImmediatePropagation()
      }
    }
    dom.addEventListener('keydown', listener, true)
    return () => dom.removeEventListener('keydown', listener, true)
  }, [editor, getEditorDom])

  // Stable IDs so the editor's aria-activedescendant points at the highlighted
  // option in the slash listbox while the menu is open.
  const reactId = useId()
  const listboxId = `slash-menu-${reactId}`
  const optionId = useCallback((idx: number) => `${listboxId}-option-${idx}`, [listboxId])
  const menuOpen = menuPhase !== 'closed' && items.length > 0
  const activeOptionId = menuOpen ? optionId(selectedIdx) : undefined

  // Wire combobox-like ARIA on the editor's contenteditable while the menu is
  // open. Removed when closed so screen readers don't reference stale elements.
  useEffect(() => {
    const dom = getEditorDom(editor)
    if (!dom) return
    if (menuOpen) {
      dom.setAttribute('aria-controls', listboxId)
      dom.setAttribute('aria-expanded', 'true')
      dom.setAttribute('aria-haspopup', 'listbox')
      if (activeOptionId) dom.setAttribute('aria-activedescendant', activeOptionId)
      else dom.removeAttribute('aria-activedescendant')
    } else {
      dom.removeAttribute('aria-controls')
      dom.removeAttribute('aria-expanded')
      dom.removeAttribute('aria-haspopup')
      dom.removeAttribute('aria-activedescendant')
    }
  }, [editor, getEditorDom, menuOpen, listboxId, activeOptionId])

  return (
    <div className="relative">
      <EditorContent editor={editor} />

      {menuOpen && (
        <SlashCommandMenu
          ref={menuRef}
          phase={menuPhase as 'command_picker' | 'note_search'}
          items={items}
          selectedIdx={selectedIdx}
          coords={menuCoords}
          listboxId={listboxId}
          optionId={optionId}
          onSelectCommand={handleSelectCommand}
          onSelectNote={handleSelectNote}
        />
      )}
    </div>
  )
}
